import React, {
	ChangeEvent,
	FormEvent,
	useEffect,
	useReducer,
	useState,
} from 'react';
import {
	getSealDetailsUrl,
	getSealByInterchange,
	sealBrands,
	sealSearchContent,
	SELECTION_ANY,
} from '../../../components/bearing-search/api/api';
import {
	SealInterchangePayload,
	SealSortPayload,
	TimkenUnitDimension,
} from '../../../components/bearing-search/types/types';
import {
	competitorColumnLabels,
	competitorResponseFields,
	text,
	unitDimensionList,
} from '../../data';
import TextInput from '../../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import SealSearchResults from '../../components/seal-search-results/SealSearchResults';
import { SelectOption } from '../../types/types';
import sealInterchangeReducer, { initialSealInterchangeState } from './reducer';
import { trackProductResults, updateActionParams } from '../../utils/analytics';
import { toggleNextTools } from '../../utils/engineering-tools';

const InterchangeSearch: React.FC = () => {
	const formDescription = sealSearchContent.partNumberDesc
		? { __html: sealSearchContent.partNumberDesc }
		: undefined;
	const [ brandList, setBrandList ] = useState<SelectOption<string>[]>( [] );
	const [ isSearched, setIsSearched ] = useState( false );
	const [ results, setResults ] = useState<string[][]>( [] );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ isRefinementOnly, setIsRefinementOnly ] = useState( false );
	const [ formState, dispatch ] = useReducer(
		sealInterchangeReducer,
		initialSealInterchangeState,
	);
	const productId = competitorResponseFields.findIndex(
		( item ) => item === 'brandPartNumber',
	);

	// Build seal brand options
	useEffect( () => {
		const emptyOption = { id: 'seal_brand_any', value: SELECTION_ANY, label: text.anyBrand, selected: true };

		const brandOptions = sealBrands.map( ( brand ) => ( {
			id: `seal_brand_${ brand.toLowerCase() }`,
			value: brand,
			label: brand,
		} ) );

		setBrandList( [ emptyOption, ...brandOptions ] );

		dispatch( { type: 'change_brand', payload: SELECTION_ANY } );
	}, [] );

	useEffect( () => {
		const isShown = isSearched && ! isResultsLoading;
		toggleNextTools( isShown );

		return () => toggleNextTools( false );
	}, [ isSearched, isResultsLoading ] );

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();

		dispatch( {
			type: 'change_validation_flag',
			payload: true,
		} );

		if ( formState.requestData ) {
			const { requestData } = formState;

			setIsSearched( true );
			setIsResultsLoading( true );
			setIsRefinementOnly( false );

			getSealByInterchange( requestData )
				.then( ( response ) => {
					const sealData = response.competitorInterchangeData;
					trackProductResults( 'sealSearch', requestData, sealData.length, formState.brandPartNumber );
					updateActionParams( 'sealSearch' );

					const tableData = sealData.map(
						( sealInfo: { [x: string]: string; } ) => {
							const row: string[] = [];
							const columnHeads = competitorResponseFields;

							for ( const key of columnHeads ) {
								row.push( sealInfo[ key ] || '-' );
							}

							return row;
						},
					);

					setResults( tableData );

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth'
					});

				} )
				.finally( () => setIsResultsLoading( false ) );
		}
	};

	const handleFormChange = () => {
		dispatch( {
			type: 'change_validation_flag',
			payload: false,
		} );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const handleSort = ( sortOptions?: SealSortPayload ) => {
		if ( ! formState.requestData ) {
			return;
		}

		const request: SealInterchangePayload = {
			...formState.requestData,
			...sortOptions,
		};

		setIsRefinementOnly( true );

		getSealByInterchange( request )
			.then( ( response ) => {
				updateActionParams( 'sealSearch' );
				const sealData = response.competitorInterchangeData;

				const tableData = sealData.map( ( sealInfo: { [x: string]: string; } ) => {
					const row: string[] = [];
					const columnHeads = competitorResponseFields;

					for ( const key of columnHeads ) {
						row.push( sealInfo[ key ]?.trim() || '-' );
					}

					return row;
				} );

				setResults( tableData );
			} )
			.finally( () => setIsResultsLoading( false ) );
	};

	const clearSearch = () => {
		setIsSearched( false );

		dispatch( {
			type: 'reset',
		} );
	};

	return (
		<div>
			<form onSubmit={handleSubmit} onChange={handleFormChange}>
				{!! formDescription && (
					<p
						className="t-engineering-tools__description"
						dangerouslySetInnerHTML={formDescription}
					></p>
				)}

				<p>
					<span className="c-form__asterisk">*</span>
					{text.requiredMessage}
				</p>

				<div className="t-engineering-tools__form-fields">
					<TextInput
						id="seal_interchange_part_number"
						required={true}
						label={text.sealSearchPartNumber}
						name="brandPartNumber"
						value={formState.brandPartNumber}
						placeholder={text.sealSearchPartNumber}
						invalid={formState.isValidated && ! formState.isPartNumberValid}
						message={text.sealWildCardHint}
						errorMessage={text.sealValidationHint}
						onChange={( event: ChangeEvent<HTMLInputElement> ) =>
							dispatch( {
								type: 'change_part_number',
								payload: event.target.value as string,
							} )
						}
					/>

					<SelectDropdown
						id="seal_interchange_brand"
						label={text.brandName}
						value={formState.brandName}
						options={brandList}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_brand',
								payload: event.target.value as string,
							} )
						}
					/>

					<SelectDropdown
						id="seal_interchange_unit"
						required={true}
						label={text.displayUnits}
						value={formState.unitDimension}
						options={unitDimensionList}
						onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
							dispatch( {
								type: 'change_unit',
								payload: event.target.value as TimkenUnitDimension,
							} )
						}
					/>
				</div>

				<div className="t-engineering-tools__search-actions">
					<button
						className="btn btn--primary"
						type="submit"
						disabled={formState.isValidated}
					>
						{text.search}
					</button>

					<button
						className="btn btn--secondary"
						type="button"
						onClick={() => clearSearch()}
					>
						{text.clear}
					</button>
				</div>
			</form>

			{isSearched && (
				<SealSearchResults
					data={results}
					refining={isRefinementOnly}
					columnKeys={competitorResponseFields as string[]}
					columnLabels={competitorColumnLabels}
					urlFieldId={productId}
					loading={isResultsLoading}
					urlComposer={getSealDetailsUrl}
					onSort={handleSort}
					unit={formState.unitDimension}
				/>
			)}
		</div>
	);
};

export default InterchangeSearch;
